import {cva} from 'cva';
import React, {PropsWithChildren, ReactElement, ReactNode} from 'react';
import {InlineSvg, RsnSkeleton} from '~/library/ui-kit';

export interface OperationDetailProps {
    label: string | ReactElement;
    variant?: 'default' | 'total';
    highlight?: boolean | undefined | 'cautionary';
    icon?: {src: string; onClick: () => void};
    children?: ReactNode;
}

const highlightVariant = (defaultText: string) => ({
    highlight: {
        true: 'text-danger-30',
        cautionary: 'text-danger-30',
        false: defaultText,
    },
});

const cls = {
    value: cva('text-right', {
        variants: {
            variant: {
                default: 'fv-text-m-regular',
                total: 'fv-heading-m',
            },
            ...highlightVariant('text-neutral-100'),
        },
        defaultVariants: {
            variant: 'default',
            highlight: false,
        },
    }),
    label: cva('fv-text-m-regular flex shrink-0 gap-1', {
        variants: {
            withIcon: {true: 'flex gap-1 items-center', false: ''},
            ...highlightVariant('text-neutral-60'),
        },
        defaultVariants: {
            withIcon: false,
            highlight: false,
        },
    }),
    box: 'flex items-baseline justify-between gap-4',
    infoButton: 'grid size-3 place-items-center',
};

export function OperationDetail({label, variant, icon, highlight, children}: OperationDetailProps) {
    const LabelTag = !icon?.onClick ? 'div' : 'button';
    if (children === '') {
        children = '—';
    }
    return (
        <div className={cls.box}>
            <LabelTag
                className={cls.label({withIcon: !!icon, highlight})}
                {...(icon?.onClick ? {type: 'button', onClick: icon.onClick} : null)}
            >
                {label}{' '}
                {icon && (
                    <div className={cls.infoButton}>
                        <InlineSvg src={icon.src} />
                    </div>
                )}
            </LabelTag>
            <div className={cls.value({variant, highlight})}>{children}</div>
        </div>
    );
}

export type OperationDetailVariantProps = Omit<OperationDetailProps, 'variant'>;

function Total(props: OperationDetailVariantProps) {
    return <OperationDetail {...props} variant='total' />;
}

Total.displayName = 'OperationDetail.Total';

function Layout({children}: PropsWithChildren) {
    return <div className='flex flex-col gap-2'>{children}</div>;
}
Layout.displayName = 'OperationDetail.Layout';

function Skeleton() {
    return <RsnSkeleton width={Math.round(Math.random() * 50 + 50)} />;
}
Skeleton.displayName = 'OperationDetail.Skeleton';

OperationDetail.Total = Total;
OperationDetail.Layout = Layout;
OperationDetail.Skeleton = React.memo(Skeleton);
